// ===== BEGIN FILE: src/components/report-v2/sections/YogasSection.tsx =====
/**
 * Yogas section showing detected classical combinations
 */

import React from 'react';
import { Box, Card, Typography, Chip } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';

export const YogasSection: React.FC<ReportSectionProps> = ({ data, onNavigate }) => {
  const { yogas } = data;

  // Group yogas by severity
  const positiveYogas = yogas.filter(y => y.severity === 'positive');
  const mixedYogas = yogas.filter(y => y.severity === 'mixed');
  const cautionYogas = yogas.filter(y => y.severity === 'caution');
  const alertYogas = yogas.filter(y => y.severity === 'alert');

  // Counts excluding admin-only private yogas (for export accuracy)
  const positiveCount = positiveYogas.filter(y => !y.private).length;
  const mixedCount = mixedYogas.filter(y => !y.private).length;
  const cautionCount = cautionYogas.filter(y => !y.private).length;
  const alertCount = alertYogas.filter(y => !y.private).length;

  // Get color for severity
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'positive':
        return 'success';
      case 'mixed':
        return 'neutral';
      case 'caution':
        return 'warning';
      case 'alert':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  // Get icon for severity
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'positive':
        return '✨';
      case 'mixed':
        return '⚖️';
      case 'caution':
        return '⚠️';
      case 'alert':
        return '🔴';
      default:
        return '📋';
    }
  };

  // Yoga type categories
  const getYogaCategory = (type: string): string => {
    if (type.includes('Raj') || type.includes('Raja')) return 'Raj Yogas (Power & Status)';
    if (type.includes('Dhana') || type.includes('Wealth')) return 'Wealth Yogas';
    if (type.includes('Mahapurusha')) return 'Mahapurusha Yogas (Great Personality)';
    if (type.includes('Parivartana') || type.includes('Exchange')) return 'Exchange Yogas';
    if (type.includes('Arishta') || type.includes('Dur')) return 'Challenging Yogas';
    if (type.includes('Marriage') || type.includes('Chandra-Mangala')) return 'Marriage & Relationship Yogas';
    return 'Other Yogas';
  };

  if (yogas.length === 0) {
    return (
      <ReportSection title="Classical Yogas" icon="🔮" pageBreakBefore onNavigate={onNavigate ? () => onNavigate('calculator') : undefined} navigateLabel="View Yogas →" navigateTab="calculator">
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            No classical yogas detected in this chart.
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
            Yogas are special planetary combinations that create unique life patterns. Your chart may have
            yogas that activate during specific dasha periods.
          </Typography>
        </Card>
      </ReportSection>
    );
  }

  // Get yoga-specific icon based on yoga name keywords
  const getYogaSpecificIcon = (cleanedName: string): string => {
    const lower = cleanedName.toLowerCase();
    if (lower.includes('gaja kesari') || lower.includes('gajakesari')) return '🐘';
    if (lower.includes('vipareeta raj') || lower.includes('viparita raj') || lower.includes('neecha bhanga')) return '👑';
    if (lower.includes('raj yoga') || lower.includes('raja yoga')) return '👑';
    if (lower.includes('hamsa')) return '🦢';
    if (lower.includes('malavya')) return '💎';
    if (lower.includes('ruchaka')) return '⚔️';
    if (lower.includes('budhaditya') || lower.includes('bhadra yoga')) return '☿️';
    if (lower.includes('sasha') || lower.includes('shasha')) return '🪐';
    if (lower.includes('lakshmi')) return '💎';
    if (lower.includes('vasumati')) return '💰';
    if (lower.includes('dhana yoga') || lower.includes('chandra mangala') || lower.includes('chandra-mangala')) return '💰';
    if (lower.includes('parivartana')) return '🔄';
    if (lower.includes('kaal sarp') || lower.includes('kala sarpa') || lower.includes('kaal_sarp')) return '🐍';
    if (lower.includes('sade sati') || lower.includes('sade-sati')) return '🪐';
    if (lower.includes('dosha')) return '🔴';
    if (lower.includes('kemdruma') || lower.includes('sakata')) return '🌑';
    if (lower.includes('amala')) return '🌟';
    if (lower.includes('adhi yoga')) return '🌟';
    if (lower.includes('sunafa') || lower.includes('anafa') || lower.includes('durudhura')) return '🌙';
    if (lower.includes('mahapurusha')) return '🏆';
    if (lower.includes('chandra yoga') || lower.includes('moon yoga')) return '🌙';
    if (lower.includes('surya yoga') || lower.includes('sun yoga')) return '☀️';
    return '';
  };

  // Extract clean yoga name — remove emojis, cut off at "ACTIVATED between" / "between" + date text
  const extractCleanName = (name: string): string => {
    let clean = name
      // Remove common emoji unicode ranges
      .replace(/[\u{1F000}-\u{1FFFF}]|[\u{2600}-\u{27BF}]|[\u{FE00}-\u{FE0F}]|[\u{200D}]/gu, '')
      .trim();
    // Cut at "ACTIVATED", "between", "Between"
    const cutWords = [' ACTIVATED', ' activated', ' between', ' Between', ' BETWEEN'];
    for (const cut of cutWords) {
      const idx = clean.indexOf(cut);
      if (idx > 0) { clean = clean.substring(0, idx); break; }
    }
    // Title-case
    return clean.trim()
      .toLowerCase()
      .split(' ')
      .filter(w => w.length > 0)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  // Yoga card component
  const YogaCard = ({ yoga }: { yoga: any }) => (
    <Card
      variant="outlined"
      className={yoga.private ? 'no-export' : undefined}
      sx={{
        p: 1.5,
        borderLeft: '4px solid',
        borderLeftColor: `${getSeverityColor(yoga.severity)}.main`,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography level="title-sm" sx={{ fontWeight: 600, flex: 1, fontSize: '0.85rem' }}>
          {(() => { const n = extractCleanName(yoga.name); const specific = getYogaSpecificIcon(n); return specific ? `${specific} ${n}` : `${getSeverityIcon(yoga.severity)} ${n}`; })()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '50%' }}>
          {yoga.isActive && (
            <Chip size="sm" color="success" variant="solid" sx={{ fontSize: '0.6rem' }}>
              Active
            </Chip>
          )}
          <Chip size="sm" color={getSeverityColor(yoga.severity)} variant="soft" sx={{ fontSize: '0.6rem' }}>
            {yoga.severity}
          </Chip>
          {yoga.isPermanent && (
            <Chip size="sm" color="primary" variant="outlined" sx={{ fontSize: '0.6rem' }}>
              Permanent
            </Chip>
          )}
        </Box>
      </Box>

      <Typography level="body-sm" sx={{ lineHeight: 1.5, fontSize: '0.75rem' }}>
        {yoga.description}
      </Typography>
    </Card>
  );

  return (
    <ReportSection title="Classical Yogas" icon="🔮" pageBreakBefore onNavigate={onNavigate ? () => onNavigate('feeds') : undefined} navigateLabel="View Yogas & Predictions →" navigateTab="feeds">
      <Box sx={{ mb: 3 }}>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          Yogas are special planetary combinations in Vedic astrology that create unique life patterns
        and karmic results. Your chart contains {yogas.filter(y => !y.private).length} detected yoga{yogas.filter(y => !y.private).length > 1 ? 's' : ''}.
        </Typography>
      </Box>

      {/* Summary counts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, rowGap: 1.5, mb: 3 }}>
        {positiveCount > 0 && (
          <Card variant="soft" color="success" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography level="h4" sx={{ fontWeight: 700 }}>{positiveCount}</Typography>
            <Typography level="body-xs">Positive</Typography>
          </Card>
        )}
        {mixedCount > 0 && (
          <Card variant="soft" color="neutral" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography level="h4" sx={{ fontWeight: 700 }}>{mixedCount}</Typography>
            <Typography level="body-xs">Mixed</Typography>
          </Card>
        )}
        {cautionCount > 0 && (
          <Card variant="soft" color="warning" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography level="h4" sx={{ fontWeight: 700 }}>{cautionCount}</Typography>
            <Typography level="body-xs">Caution</Typography>
          </Card>
        )}
        {alertCount > 0 && (
          <Card variant="soft" color="danger" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography level="h4" sx={{ fontWeight: 700 }}>{alertCount}</Typography>
            <Typography level="body-xs">Alert</Typography>
          </Card>
        )}
      </Box>

      {/* Positive yogas */}
      {positiveYogas.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography level="title-lg" sx={{ mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            ✨ Positive Yogas
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, rowGap: 1.5, alignItems: 'start' }}>
            {positiveYogas.map((yoga, index) => (
              <YogaCard key={index} yoga={yoga} />
            ))}
          </Box>
        </Box>
      )}

      {/* Mixed yogas */}
      {mixedYogas.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography level="title-lg" sx={{ mb: 2, color: 'neutral.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            ⚖️ Mixed Yogas
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, rowGap: 1.5, alignItems: 'start' }}>
            {mixedYogas.map((yoga, index) => (
              <YogaCard key={index} yoga={yoga} />
            ))}
          </Box>
        </Box>
      )}

      {/* Caution yogas */}
      {cautionYogas.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography level="title-lg" sx={{ mb: 2, color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            ⚠️ Caution Yogas
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, rowGap: 1.5, alignItems: 'start' }}>
            {cautionYogas.map((yoga, index) => (
              <YogaCard key={index} yoga={yoga} />
            ))}
          </Box>
        </Box>
      )}

      {/* Alert yogas */}
      {alertYogas.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography level="title-lg" sx={{ mb: 2, color: 'danger.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            🔴 Alert Yogas
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, rowGap: 1.5, alignItems: 'start' }}>
            {alertYogas.map((yoga, index) => (
              <YogaCard key={index} yoga={yoga} />
            ))}
          </Box>
        </Box>
      )}

      {/* Understanding Yogas */}
      <Card
        variant="soft"
        sx={{
          p: 3,
          mt: 4,
          background: 'linear-gradient(135deg, rgba(91, 33, 182, 0.05), rgba(245, 158, 11, 0.05))',
        }}
      >
        <Typography level="title-md" sx={{ mb: 2, color: 'primary.main' }}>
          📚 Understanding Yogas
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Permanent Yogas:</strong> These are natal yogas formed by planetary placements at birth.
            They remain active throughout life but may express differently during various dasha periods.
          </Typography>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Activation:</strong> Yogas become most prominent when their ruling planets have active
            dasha periods (Mahadasha, Antardasha, or Pratyantar Dasha).
          </Typography>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Interpretation:</strong> Positive yogas bring opportunities and natural talents.
            Challenging yogas indicate areas requiring conscious effort and awareness for growth.
          </Typography>
        </Box>
      </Card>
    </ReportSection>
  );
};
